import { PortalContainerProvider, SkyDropdown } from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import type { MeInstance } from "@shared/types";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";

/**
 * Which tenant and which instance the user is working in (FR-20, owner round 3, 2026-09-30).
 *
 * The Skypoint standard, as skyAdvisor's profile menu builds it (`ProfileMenu.tsx`,
 * `TenantInstanceSwitcher.tsx`): a "Tenant" `SkyDropdown`, then an "Instance" `SkyDropdown`
 * holding the chosen tenant's instances. It replaced a flat list with one row per tenant-instance
 * pair, which does not scale to many tenants. It sits in the user menu, the shell's one picker
 * since round 4 (2026-09-30: the top bar's label is plain text). There is no library component for
 * it yet (docs/backlog.md, "A SkyTenantInstanceSwitcher in skypoint-ui").
 *
 * Choosing a tenant only narrows the Instance list; nothing a request carries changes until an
 * instance is picked, and that calls `selectInstance` exactly as every other way in does (the
 * stored key, the `x-instance-id` header and the person's remembered last instance are
 * `TenantInstanceContext`'s).
 *
 * **Search.** `SkyDropdown` (2.17.0) always offers its own search box, on every list; it has no
 * switch to hide it on a short one. It is what finds "Northwind" among many tenants.
 *
 * **Portals.** The dropdowns' lists portal into this picker rather than the page body: the
 * picker sits inside a `SkyPopover` (z-index 1060), and a list portalled to the body at the
 * dropdown layer (500) would open behind it. This is what `SkyDrawer` does for its own content.
 */

interface TenantOption {
  id: string;
  name: string;
}

const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });

/**
 * Each tenant once, named by its Display name (never its Name, owner 2026-10-01), in name order
 * (then by id, so two with one name keep an order). #826: Skypoint's canary tenant says so, as the
 * Platform screens' badge does; a dropdown option is text, so the word follows the name.
 */
function tenantsOf(instances: readonly MeInstance[]): TenantOption[] {
  const seen = new Map<string, TenantOption>();
  for (const instance of instances) {
    if (!seen.has(instance.tenantId)) {
      const name =
        instance.tenantPurpose === "canary"
          ? `${instance.tenantDisplayName} (Canary)`
          : instance.tenantDisplayName;
      seen.set(instance.tenantId, { id: instance.tenantId, name });
    }
  }
  return [...seen.values()].sort((a, b) => byName(a.name, b.name) || a.id.localeCompare(b.id));
}

/** One tenant's instances, in name order. */
function instancesOfTenant(
  instances: readonly MeInstance[],
  tenantId: string | null,
): MeInstance[] {
  if (!tenantId) return [];
  return instances
    .filter((instance) => instance.tenantId === tenantId)
    .sort((a, b) => byName(a.name, b.name) || a.id.localeCompare(b.id));
}

export interface TenantInstancePickerProps {
  /** Called once an instance is picked, so the surrounding popover can close. */
  onChosen?: () => void;
  className?: string;
}

export function TenantInstancePicker({ onChosen, className }: TenantInstancePickerProps) {
  const { instances, selectedInstance, selectInstance } = useTenantInstance();
  const [tenantChoice, setTenantChoice] = useState<string | null>(null);
  const [portal, setPortal] = useState<HTMLDivElement | null>(null);

  const tenants = useMemo(() => tenantsOf(instances), [instances]);
  const onlyTenant = tenants.length === 1 ? (tenants[0]?.id ?? null) : null;
  const tenantId = tenantChoice ?? selectedInstance?.tenantId ?? onlyTenant;
  const tenantInstances = useMemo(
    () => instancesOfTenant(instances, tenantId),
    [instances, tenantId],
  );
  const selectedHere =
    selectedInstance && selectedInstance.tenantId === tenantId ? selectedInstance.id : null;
  // Nothing to choose: the tenant's one instance is the one already selected.
  const instanceFixed =
    tenantInstances.length === 1 && tenantInstances[0]?.id === selectedInstance?.id;

  return (
    <div className={className} data-testid="tenant-instance-picker">
      <div className="flex flex-col gap-3">
        <PortalContainerProvider value={portal}>
          <SkyDropdown
            label="Tenant"
            testId="tenant-instance-picker-tenant"
            placeholder="Choose a tenant"
            options={tenants.map((tenant) => ({ value: tenant.id, text: tenant.name }))}
            selectedOptions={tenantId ? [tenantId] : []}
            disabled={tenants.length <= 1}
            onOptionSelect={(values) => {
              const next = values[0];
              if (next) setTenantChoice(next);
            }}
          />
          <SkyDropdown
            label="Instance"
            testId="tenant-instance-picker-instance"
            placeholder="Choose an instance"
            options={tenantInstances.map((instance) => ({
              value: instance.id,
              // #419: an instance still being set up, or whose setup failed, says so, as the
              // picker page's badge does.
              text:
                instance.status === "provisioning"
                  ? `${instance.name} (Provisioning)`
                  : instance.status === "setup_failed"
                    ? `${instance.name} (Setup failed)`
                    : instance.name,
            }))}
            selectedOptions={selectedHere ? [selectedHere] : []}
            disabled={!tenantId || tenantInstances.length === 0 || instanceFixed}
            onOptionSelect={(values) => {
              const next = values[0];
              if (!next) return;
              if (next !== selectedInstance?.id) selectInstance(next);
              onChosen?.();
            }}
          />
        </PortalContainerProvider>
        {/* Round 6 T-08 (owner 2026-10-01): said once, here, with no setting to turn it off. */}
        <p className="text-xs text-muted-foreground" data-testid="tenant-instance-picker-note">
          You land on the instance you used last.
        </p>
      </div>
      {/* The dropdowns' lists open here, inside the popover's own layer. */}
      <div ref={setPortal} />
    </div>
  );
}
